/**
 * aspicPipeline — gelatin jelly with trapped flowers/objects.
 *
 * A clear jelly-like volume with multiple small objects (flowers,
 * leaves, geometric shapes) suspended inside it. The objects are
 * rendered with refraction through the jelly, so they appear at
 * slightly distorted positions and colors. The jelly surface has
 * subtle ripples and reflections.
 *
 * Innovations:
 *   - Each trapped object is a 2D SDF (circle, star, square) with
 *     a pseudo-3D position (x, y, depth). The depth determines the
 *     refraction offset.
 *   - The jelly surface is a wavy boundary at the edges of the
 *     screen, with internal caustic-like light patterns.
 *   - Each object's color is preserved but tinted by the jelly's
 *     color (warm honey amber).
 *   - The objects have a slight apparent sway (drift) as if the
 *     jelly is gently moving.
 *
 * Audio:
 *   - bass: jelly sway (objects drift more)
 *   - mid:  jelly color saturation
 *   - high: surface ripple intensity
 */
import { linkProgram, POST_ATTRIBS } from "./glContext";

export interface AspicPipeline {
  program: WebGLProgram;
  vao: WebGLVertexArrayObject | null;
  vbo: WebGLBuffer | null;
  uResolution: WebGLUniformLocation | null;
  uTime: WebGLUniformLocation | null;
  uBass: WebGLUniformLocation | null;
  uMid: WebGLUniformLocation | null;
  uHigh: WebGLUniformLocation | null;
  bands: Float32Array;
}

const VERT_SRC = `#version 300 es
precision highp float;
layout(location = 0) in vec2 aPos;
out vec2 vUV;
void main() {
  vUV = aPos * 0.5 + 0.5;
  gl_Position = vec4(aPos, 0.0, 1.0);
}`;

const FRAG_SRC = `#version 300 es
precision highp float;
in vec2 vUV;
out vec4 outColor;

uniform vec2 uResolution;
uniform float uTime;
uniform vec3 uBass;
uniform vec3 uMid;
uniform vec3 uHigh;

float hash21(vec2 p) {
  p = fract(p * vec2(123.34, 456.21));
  p += dot(p, p + 45.32);
  return fract(p.x * p.y);
}

float valueNoise(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  f = f * f * (3.0 - 2.0 * f);
  float a = hash21(i);
  float b = hash21(i + vec2(1.0, 0.0));
  float c = hash21(i + vec2(0.0, 1.0));
  float d = hash21(i + vec2(1.0, 1.0));
  return mix(mix(a, b, f.x), mix(c, d, f.x), f.y);
}

// Per-object: returns (distance, color, depth).
// Depth is in [0, 1] — 0 = near, 1 = far.
vec4 flowerObj(vec2 p, float i, float t, float sway) {
  // Center position — sways gently.
  float cx = sin(t * 0.3 + i * 1.7) * 0.4 * sway;
  float cy = cos(t * 0.4 + i * 2.3) * 0.4 * sway;
  // Each flower has its own size and color.
  float r = 0.04 + 0.030 * hash21(vec2(i, 1.0));
  // 5-petal flower SDF.
  vec2 lp = p - vec2(cx, cy);
  float ang = atan(lp.y, lp.x);
  float rad = length(lp);
  float petals = 5.0;
  float petalPhase = ang * petals + t * 0.5;
  float petalR = r * (1.0 + 0.5 * cos(petalPhase));
  float d = rad - petalR;
  // Color: pick from a flower palette.
  vec3 c1 = vec3(0.95, 0.30, 0.45);
  vec3 c2 = vec3(0.95, 0.75, 0.30);
  vec3 c3 = vec3(0.60, 0.40, 0.95);
  vec3 c4 = vec3(0.95, 0.45, 0.75);
  vec3 c5 = vec3(0.40, 0.85, 0.95);
  vec3 col = c1;
  if (mod(i, 5.0) < 1.0) col = c1;
  else if (mod(i, 5.0) < 2.0) col = c2;
  else if (mod(i, 5.0) < 3.0) col = c3;
  else if (mod(i, 5.0) < 4.0) col = c4;
  else col = c5;
  // Depth — used for refraction offset.
  float depth = fract(hash21(vec2(i, 7.0)) * 7.0);
  return vec4(d, col.r, col.g, col.b);
}

vec4 leafObj(vec2 p, float i, float t, float sway) {
  float cx = cos(t * 0.2 + i * 1.3) * 0.5 * sway;
  float cy = sin(t * 0.35 + i * 1.9) * 0.5 * sway;
  float r = 0.05 + 0.025 * hash21(vec2(i, 2.0));
  vec2 lp = p - vec2(cx, cy);
  // Rotate the leaf.
  float rot = t * 0.2 + i * 2.0;
  float ca = cos(rot);
  float sa = sin(rot);
  vec2 rlp = vec2(ca * lp.x - sa * lp.y, sa * lp.x + ca * lp.y);
  // Leaf shape: ellipse.
  float d = length(rlp / vec2(r * 2.0, r)) - 0.5;
  vec3 col = vec3(0.30, 0.65, 0.40);
  return vec4(d, col.r, col.g, col.b);
}

vec4 gemObj(vec2 p, float i, float t, float sway) {
  float cx = sin(t * 0.25 + i * 1.7) * 0.55 * sway;
  float cy = cos(t * 0.3 + i * 2.1) * 0.55 * sway;
  float r = 0.04 + 0.025 * hash21(vec2(i, 3.0));
  vec2 lp = p - vec2(cx, cy);
  float ang = atan(lp.y, lp.x);
  // 6-sided gem.
  float sides = 6.0;
  float sec = abs(cos(ang * sides * 0.5 + t * 0.4));
  float rad = length(lp);
  float d = rad - r * (0.7 + 0.3 * sec);
  vec3 col = vec3(0.75, 0.85, 0.95);
  return vec4(d, col.r, col.g, col.b);
}

void main() {
  float bass = (uBass.x + uBass.y + uBass.z) / 3.0;
  float mid = (uMid.x + uMid.y + uMid.z) / 3.0;
  float high = (uHigh.x + uHigh.y + uHigh.z) / 3.0;
  bass = pow(clamp(bass * 2.5, 0.0, 1.0), 0.7);
  mid = pow(clamp(mid * 3.0, 0.0, 1.0), 0.7);
  high = pow(clamp(high * 4.0, 0.0, 1.0), 0.7);

  vec2 p = (gl_FragCoord.xy - uResolution * 0.5) / uResolution.y;

  // Background — dark with a faint glow.
  float r = length(p);
  vec3 bg = mix(
    vec3(0.10, 0.06, 0.04),
    vec3(0.02, 0.01, 0.01),
    smoothstep(0.0, 0.7, r)
  );

  // Jelly color — warm honey amber.
  vec3 jellyColor = vec3(0.95, 0.65, 0.30);
  vec3 jellyColorDeep = vec3(0.85, 0.40, 0.20);

  // Jelly body — a large rounded rectangle filling most of the screen.
  // The "edge" is the outer boundary of the jelly.
  vec2 jellySize = vec2(0.85, 0.95);
  vec2 jellyD = abs(p) - jellySize;
  // Ripple the jelly edge.
  float edgeAngle = atan(p.y, p.x);
  float ripple = 0.020 * sin(edgeAngle * 8.0 + uTime * 2.0) +
                 0.012 * sin(edgeAngle * 13.0 - uTime * 1.5);
  jellyD.x += ripple * 0.5;
  jellyD.y += ripple * 0.5;
  float jellyMask = smoothstep(0.005, -0.005, max(jellyD.x, jellyD.y));
  // Jelly interior shading — gradient from center to edge.
  float jellyDist = length(p / jellySize);
  vec3 jellyInterior = mix(jellyColor, jellyColorDeep, jellyDist * 0.5);

  // Refractive distortion: shift the texture sample based on the
  // gradient of the jelly boundary.
  vec2 refract = vec2(0.0);
  float eps = 0.005;
  float j00 = length(p) - 0.6;
  float j10 = length(p + vec2(eps, 0.0)) - 0.6;
  float j01 = length(p + vec2(0.0, eps)) - 0.6;
  refract = vec2(j10 - j00, j01 - j00) * 8.0;

  vec3 col = bg;
  // Trapped objects — drawn before the jelly tint.
  // Draw 8 flowers, 6 leaves, 5 gems.
  for (int i = 0; i < 8; i++) {
    vec4 f = flowerObj(p - refract * 0.5, float(i), uTime, 0.8 + bass * 0.4);
    float mask = smoothstep(0.005, -0.005, f.x);
    col = mix(col, vec3(f.yzw), mask);
  }
  for (int i = 0; i < 6; i++) {
    vec4 l = leafObj(p - refract * 0.5, float(i), uTime, 0.7 + bass * 0.3);
    float mask = smoothstep(0.005, -0.005, l.x);
    col = mix(col, vec3(l.yzw), mask);
  }
  for (int i = 0; i < 5; i++) {
    vec4 g = gemObj(p - refract * 0.5, float(i), uTime, 0.9 + bass * 0.2);
    float mask = smoothstep(0.005, -0.005, g.x);
    col = mix(col, vec3(g.yzw), mask);
  }

  // Apply jelly tint (alpha blend).
  col = mix(col, jellyInterior, jellyMask * 0.45);

  // Jelly edge highlight (a thin bright rim).
  float edge = 1.0 - smoothstep(0.0, 0.015, max(jellyD.x, jellyD.y));
  float edgeRim = smoothstep(0.015, 0.0, max(jellyD.x, jellyD.y));
  col += vec3(1.0, 0.95, 0.7) * edgeRim * 0.6;

  // Surface ripples inside the jelly — caustic-like patterns.
  float surface = 0.0;
  for (int i = 0; i < 4; i++) {
    float fi = float(i);
    vec2 sp = vec2(
      sin(uTime * 0.3 + fi * 1.7),
      cos(uTime * 0.27 + fi * 2.1)
    ) * 0.5;
    float rippleD = length(p - sp);
    surface += exp(-rippleD * 5.0) * (0.5 + 0.5 * sin(rippleD * 20.0 - uTime * 3.0));
  }
  // Only inside the jelly.
  col += vec3(1.0, 0.85, 0.5) * surface * jellyMask * (0.2 + 0.4 * high);

  // Bubbles trapped inside.
  for (int i = 0; i < 12; i++) {
    float fi = float(i);
    vec2 bp = vec2(
      sin(uTime * 0.2 + fi * 1.7) * 0.6,
      cos(uTime * 0.23 + fi * 2.1) * 0.7
    );
    float br = 0.005 + 0.015 * hash21(vec2(fi, 5.0));
    float bd = length(p - bp);
    float bubble = exp(-abs(bd - br) * 200.0);
    col += vec3(1.0, 0.95, 0.8) * bubble * jellyMask * 0.5;
  }

  // Tone mapping.
  col = (col * (2.51 * col + 0.03)) / (col * (2.43 * col + 0.59) + 0.14);
  col = pow(clamp(col, 0.0, 1.0), vec3(1.0 / 2.2));

  // Vignette.
  col *= 1.0 - smoothstep(0.5, 1.0, r) * 0.4;

  outColor = vec4(col, 1.0);
}
`;

export function computeAspicBands(t: number, out: Float32Array): void {
  out[0] = 0.5 + 0.3 * Math.sin(t * 0.5);
  out[1] = 0.5 + 0.3 * Math.sin(t * 0.8 + 1.0);
  out[2] = 0.4 + 0.3 * Math.sin(t * 1.1 + 2.0);
  out[3] = 0.5 + 0.3 * Math.sin(t * 1.3 + 1.5);
  out[4] = 0.5 + 0.3 * Math.sin(t * 1.6 + 0.5);
  out[5] = 0.4 + 0.3 * Math.sin(t * 1.9 + 2.5);
  out[6] = 0.3 + 0.4 * Math.sin(t * 2.4 + 0.7);
  out[7] = 0.4 + 0.3 * Math.sin(t * 2.8 + 1.8);
  out[8] = 0.3 + 0.4 * Math.sin(t * 3.3 + 3.0);
}

export function computeAspicRealtimeBands(
  freqData: Uint8Array,
  out: Float32Array,
): void {
  const N = freqData.length;
  const ranges = [
    [0.00, 0.030], [0.030, 0.080], [0.080, 0.150],
    [0.150, 0.260], [0.260, 0.380], [0.380, 0.500],
    [0.500, 0.660], [0.660, 0.820], [0.820, 0.980],
  ];
  for (let i = 0; i < 9; i++) {
    const [lo, hi] = ranges[i];
    const start = Math.floor(lo * N);
    const end = Math.floor(hi * N);
    let sum = 0, count = 0;
    for (let k = start; k < end; k++) {
      sum += freqData[k];
      count++;
    }
    out[i] = count > 0 ? (sum / count) / 255 : 0;
  }
}

export function createAspicPipeline(gl: WebGL2RenderingContext): AspicPipeline | null {
  const program = linkProgram(gl, VERT_SRC, FRAG_SRC, POST_ATTRIBS);
  if (!program) return null;
  const vao = gl.createVertexArray();
  const vbo = gl.createBuffer();
  if (!vao || !vbo) return null;
  const verts = new Float32Array([
    -1, -1,  1, -1,  -1, 1,
    -1,  1,  1, -1,   1, 1,
  ]);
  gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
  gl.bufferData(gl.ARRAY_BUFFER, verts, gl.STATIC_DRAW);
  gl.enableVertexAttribArray(POST_ATTRIBS.aPos);
  gl.vertexAttribPointer(POST_ATTRIBS.aPos, 2, gl.FLOAT, false, 0, 0);
  gl.bindVertexArray(null);
  return {
    program, vao, vbo,
    uResolution: gl.getUniformLocation(program, "uResolution"),
    uTime: gl.getUniformLocation(program, "uTime"),
    uBass: gl.getUniformLocation(program, "uBass"),
    uMid: gl.getUniformLocation(program, "uMid"),
    uHigh: gl.getUniformLocation(program, "uHigh"),
    bands: new Float32Array(9),
  };
}

export function drawAspic(
  gl: WebGL2RenderingContext,
  pipe: AspicPipeline,
  width: number,
  height: number,
  t: number,
): void {
  gl.useProgram(pipe.program);
  const prog = pipe.program;
  gl.uniform2f(gl.getUniformLocation(prog, "uResolution"), width, height);
  gl.uniform1f(gl.getUniformLocation(prog, "uTime"), t);
  gl.uniform3f(gl.getUniformLocation(prog, "uBass"), pipe.bands[0], pipe.bands[1], pipe.bands[2]);
  gl.uniform3f(gl.getUniformLocation(prog, "uMid"), pipe.bands[3], pipe.bands[4], pipe.bands[5]);
  gl.uniform3f(gl.getUniformLocation(prog, "uHigh"), pipe.bands[6], pipe.bands[7], pipe.bands[8]);
  gl.bindVertexArray(pipe.vao);
  gl.drawArrays(gl.TRIANGLES, 0, 6);
  gl.bindVertexArray(null);
}
